import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { InvestmentsAPI } from '../services/investments.ts';
import { formatCurrency, getCurrencyLocale, safeCurrencyCode } from '../utils/currency.ts';
import {
  importSourceLabel,
  investmentCategoryLabel,
  priceMovementDescription,
} from '../utils/labels.ts';
import { formatStatus } from '../utils/statusFormatter.ts';
import StatusChip from '../components/ui/StatusChip.tsx';
import ConfirmationModal from '../components/ConfirmationModal.tsx';
import AssetSparkline from '../components/investments/AssetSparkline.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';

const InvestmentDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'resumo' | 'historico' | 'documentos'>('resumo');
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const {
    data: item,
    isLoading,
    error,
  } = useQuery(['investment', id], () => InvestmentsAPI.get(String(id)), { enabled: !!id });

  const { data: history = [] } = useQuery(
    ['investment-price-history', id],
    () => InvestmentsAPI.priceHistory(String(id)),
    { enabled: !!id }
  );

  const { data: portfolio } = useQuery(
    'investments-portfolio',
    () => InvestmentsAPI.portfolioCharts(),
    {
      staleTime: 60_000,
    }
  );

  const deleteMutation = useMutation(() => InvestmentsAPI.remove(String(id)), {
    onSuccess: () => {
      queryClient.invalidateQueries(['investments']);
      queryClient.invalidateQueries(['investments-portfolio']);
      navigate('/investments');
    },
  });

  const currency = safeCurrencyCode(item?.currency);
  const money = (v: number) => formatCurrency(v, currency, getCurrencyLocale(currency));

  const weightPct = useMemo(() => {
    if (!item || !portfolio?.total) return null;
    return (Number(item.currentValue) / portfolio.total) * 100;
  }, [item, portfolio]);

  const sparkPoints = useMemo(
    () => history.map(h => ({ timestamp: h.timestamp, valor: h.valor })),
    [history]
  );

  const recentMoves = useMemo(() => {
    const sorted = [...history].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
    return sorted.slice(0, 8);
  }, [history]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }
  if (error || !item) {
    return <div className="field-error">Investimento não encontrado.</div>;
  }

  const ultimaLabel = item.ultimaAtualizacao
    ? new Date(item.ultimaAtualizacao).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  return (
    <div>
      <div className="breadcrumb" style={{ marginBottom: 4 }}>
        Patrimônio / Investimentos / {item.description}
      </div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 8,
        }}
      >
        <div>
          <h1
            className="page-title"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}
          >
            {item.description}
            <StatusChip
              label={formatStatus(item.status)}
              tone={item.status === 'active' ? 'green' : 'slate'}
            />
          </h1>
          <div className="page-subtitle">Detalhe do investimento</div>
        </div>
      </div>

      <div className="toolbar" style={{ marginBottom: 12 }}>
        <div className="filters">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => navigate('/investments')}
          >
            ← Voltar aos investimentos
          </button>
        </div>
        <div className="detail-actions">
          {hasPermission('investments.update') && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate(`/investments/${item.id}/editar`)}
            >
              Editar
            </button>
          )}
          {hasPermission('investments.update') && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate(`/investments/${item.id}/atualizar-preco`)}
            >
              Atualizar preço
            </button>
          )}
          {hasPermission('investments.delete') && (
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => setShowDeleteModal(true)}
            >
              Excluir
            </button>
          )}
        </div>
      </div>

      <div className="tabs" style={{ marginBottom: 12 }}>
        <button
          type="button"
          className={`tab${tab === 'resumo' ? ' active' : ''}`}
          onClick={() => setTab('resumo')}
        >
          Resumo
        </button>
        <button
          type="button"
          className={`tab${tab === 'historico' ? ' active' : ''}`}
          onClick={() => setTab('historico')}
        >
          Histórico
        </button>
        <button
          type="button"
          className={`tab${tab === 'documentos' ? ' active' : ''}`}
          onClick={() => setTab('documentos')}
        >
          Documentos
        </button>
      </div>

      {tab === 'documentos' ? (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Documentos</div>
          </div>
          <div style={{ padding: 32, textAlign: 'center' }} className="muted">
            Anexos de investimentos ainda não estão disponíveis neste pacote.
          </div>
        </div>
      ) : tab === 'historico' ? (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Histórico de preço</div>
            <span className="muted">{history.length} pontos</span>
          </div>
          {history.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center' }} className="muted">
              Sem histórico. Use <strong>Atualizar preço</strong> para acrescentar o 1º ponto.
            </div>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Fonte</th>
                    <th style={{ textAlign: 'right' }}>Preço unit.</th>
                    <th style={{ textAlign: 'right' }}>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {[...history]
                    .sort(
                      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
                    )
                    .map(h => (
                      <tr key={h.id}>
                        <td className="mono">
                          {new Date(h.timestamp).toLocaleString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                        <td>{importSourceLabel(h.fonte)}</td>
                        <td style={{ textAlign: 'right' }}>
                          {h.preco != null ? money(h.preco) : '—'}
                        </td>
                        <td className="amount" style={{ textAlign: 'right' }}>
                          {money(h.valor)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <>
          <div
            className="alert-row"
            style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12, marginBottom: 12 }}
          >
            {ultimaLabel && (
              <div className="alert-banner green">
                <div>
                  <strong>Última atualização {ultimaLabel}</strong>
                  Valor de mercado sincronizado
                </div>
              </div>
            )}
          </div>

          <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            <div className="kpi">
              <div className="kpi-label">Valor de mercado</div>
              <div className="kpi-value">{money(item.currentValue)}</div>
            </div>
            <div className="kpi">
              <div className="kpi-label">Custo / aplicação</div>
              <div className="kpi-value">{money(item.investedValue)}</div>
            </div>
            <div className="kpi">
              <div className="kpi-label">Rendimento</div>
              <div
                className="kpi-value"
                style={{ color: item.yieldPercent >= 0 ? '#16a34a' : '#dc2626' }}
              >
                {item.yieldPercent >= 0 ? '+' : ''}
                {item.yieldPercent.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%
              </div>
              <div className="kpi-hint">{money(item.yieldAmount)}</div>
            </div>
            <div className="kpi">
              <div className="kpi-label">Peso na carteira</div>
              <div className="kpi-value">
                {weightPct != null
                  ? `${weightPct.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`
                  : '—'}
              </div>
              <div className="kpi-hint">{investmentCategoryLabel(item.category)}</div>
            </div>
          </div>

          <div
            className="grid-2"
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}
          >
            <div className="card invest-detail-card">
              <div className="card-header">
                <div className="card-title">Dados do investimento</div>
                {hasPermission('investments.update') && (
                  <Link to={`/investments/${item.id}/editar`} className="link-btn">
                    Editar
                  </Link>
                )}
              </div>
              <dl className="invest-detail-fields">
                <div className="span-2">
                  <dt>Nome</dt>
                  <dd>{item.description}</dd>
                </div>
                <div>
                  <dt>Categoria</dt>
                  <dd>{investmentCategoryLabel(item.category)}</dd>
                </div>
                <div>
                  <dt>Ticker</dt>
                  <dd className="mono">{item.ticker || '—'}</dd>
                </div>
                <div>
                  <dt>Quantidade</dt>
                  <dd className="mono">
                    {item.quantity != null
                      ? Number(item.quantity).toLocaleString('pt-BR', { maximumFractionDigits: 6 })
                      : '—'}
                  </dd>
                </div>
                <div>
                  <dt>Preço unitário</dt>
                  <dd>{item.unitPrice != null ? money(item.unitPrice) : '—'}</dd>
                </div>
                <div>
                  <dt>Moeda</dt>
                  <dd>{currency}</dd>
                </div>
                <div>
                  <dt>Status</dt>
                  <dd>
                    <StatusChip
                      label={formatStatus(item.status)}
                      tone={item.status === 'active' ? 'green' : 'slate'}
                    />
                  </dd>
                </div>
              </dl>
            </div>
            <div className="card invest-detail-card">
              <div className="card-header">
                <div className="card-title">Histórico de preço</div>
                <span className="muted">evolução recente</span>
              </div>
              <AssetSparkline points={sparkPoints} empty={sparkPoints.length === 0} />
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">Movimentações recentes</div>
              <button type="button" className="link-btn" onClick={() => setTab('historico')}>
                Ver todas
              </button>
            </div>
            {recentMoves.length === 0 ? (
              <div style={{ padding: 28, textAlign: 'center' }} className="muted">
                Nenhuma movimentação registrada.
              </div>
            ) : (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Tipo</th>
                      <th>Descrição</th>
                      <th style={{ textAlign: 'right' }}>Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentMoves.map(h => (
                      <tr key={h.id}>
                        <td className="mono">
                          {new Date(h.timestamp).toLocaleDateString('pt-BR')}
                        </td>
                        <td>
                          <StatusChip
                            label={h.fonte === 'manual' ? 'Atualização' : 'Importação'}
                            tone="blue"
                          />
                        </td>
                        <td>{priceMovementDescription(h.fonte)}</td>
                        <td className="amount" style={{ textAlign: 'right' }}>
                          {money(h.valor)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Excluir investimento"
        message="Tem certeza que deseja excluir este investimento? O histórico de preços também será removido."
        onConfirm={() => deleteMutation.mutate()}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
        isLoading={deleteMutation.isLoading}
      />
    </div>
  );
};

export default InvestmentDetail;
